"use client";

import { Button } from "@/components/Button";
import { getDataLinkAttributes } from "@/helpers";
import dynamic from "next/dynamic";
import { FC, useState } from "react";
import { DialogModal } from "@/components/Modal";

const PDFDownloadLink = dynamic(() =>
  import("./PdfViewer").then((mod) => mod.PDFDownloadLink),
);

const BlobProvider = dynamic(() =>
  import("./PdfViewer").then((mod) => mod.BlobProvider),
);

const ItsmPdfTemplate = dynamic(() =>
  import("./ItsmPdfTemplate").then((mod) => mod.default),
);

type PdfButtonConfig = {
  buttonText: string;
  dld?: string;
  dlt?: string;
};

export type PdfButtonsProps = {
  pdfButtons: {
    downloadButton: PdfButtonConfig;
    printButton: PdfButtonConfig;
    startNewModelButton: PdfButtonConfig;
  };
  clearModal: {
    headline: string;
    description: string;
    backButtonText: string;
    startNewButtonText: string;
  };
  clearModel: () => void;
  pdf: {
    image: string;
    title: string;
    name: string;
    company: string;
    challenge?: string;
  };
};

const ItsmPdfButtons: FC<PdfButtonsProps> = ({
  pdfButtons,
  clearModal,
  clearModel,
  pdf,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleStartNewModel = () => {
    clearModel();
    closeModal();
  };

  return (
    <div className="px-6 py-10">
      <div className="mx-auto flex w-full flex-row items-center justify-center gap-8">
        <PDFDownloadLink
          className="text-button-primary-content bg-button-primary-fill hover:text-button-primary-content-hover hover:bg-button-primary-fill-hover active:text-button-primary-content-pressed active:bg-button-primary-fill-pressed disabled:text-button-primary-content-disabled disabled:bg-button-primary-fill-disabled text-link-m-mobile sm:text-link-m rounded-button px-6 py-3 font-bold disabled:cursor-not-allowed sm:py-4"
          fileName="ITSM Maturity Model Summary"
          document={
            <ItsmPdfTemplate
              dateType="US"
              title={pdf.title}
              name={pdf.name}
              company={pdf.company}
              challenge={pdf.challenge}
              imageSrc={pdf.image}
            />
          }
          {...getDataLinkAttributes(pdfButtons.downloadButton)}
          data-automation-group="cta-button"
        >
          {pdfButtons.downloadButton.buttonText}
        </PDFDownloadLink>
        <BlobProvider
          document={
            <ItsmPdfTemplate
              dateType="US"
              title={pdf.title}
              name={pdf.name}
              company={pdf.company}
              challenge={pdf.challenge}
              imageSrc={pdf.image}
            />
          }
        >
          {({ url }) =>
            url ? (
              <Button
                variant="secondary"
                size="m"
                href={url}
                target="_blank"
                {...getDataLinkAttributes(pdfButtons.printButton)}
              >
                {pdfButtons.printButton.buttonText}
              </Button>
            ) : null
          }
        </BlobProvider>
        <Button
          variant="tertiary"
          icon="arrowRight"
          iconPosition="right"
          size="m"
          onClick={openModal}
          {...getDataLinkAttributes(pdfButtons.startNewModelButton)}
          data-automation-group="cta-button"
        >
          {pdfButtons.startNewModelButton.buttonText}
        </Button>
      </div>
      <DialogModal
        open={isModalOpen}
        onClose={closeModal}
        panelClassName="max-w-146"
      >
        <p className="text-subhead-m text-swdc-black-600 font-bold">
          {clearModal.headline}
        </p>
        <div className="border-swdc-silver-300 mt-6 w-full border-t pt-6">
          <p className="text-body-m text-swdc-gray-400">
            {clearModal.description}
          </p>
        </div>
        <div className="mt-10 flex flex-row justify-between">
          <Button
            variant="primary"
            size="m"
            onClick={closeModal}
            data-automation-group="cta-button"
          >
            {clearModal.backButtonText}
          </Button>
          <Button
            variant="secondary"
            size="m"
            onClick={handleStartNewModel}
            data-automation-group="cta-button"
          >
            {clearModal.startNewButtonText}
          </Button>
        </div>
      </DialogModal>
    </div>
  );
};

export default ItsmPdfButtons;
